(function () {
  const sectionInstances = new WeakMap();

  class PopularProductsSection {
    constructor(section) {
      this.section = section;
      this.container = section.querySelector(".popular-products");
      this.layoutEl = this.container?.querySelector(
        ".popular-products__layout"
      );
      this.wrapper = this.container?.querySelector(
        ".popular-products__wrapper"
      );

      if (!this.container || !this.layoutEl || !this.wrapper) {
        return;
      }

      this.layout =
        this.layoutEl.dataset.layout || this.container.dataset.layout || "grid";
      this.filterItems = Array.from(
        this.section.querySelectorAll("[data-filter-target]")
      );
      this.allSlides = Array.from(this.wrapper.children);
      this.perRow = Number(this.layoutEl.dataset.cardsPerRow || 1);
      this.breakpointMap = this.buildBreakpointMap();
      this.activeFilter = this.getInitialFilter();
      this.swiper = null;
      this.resizeObserver = null;

      this.bindFilters();

      if (this.layout === "slider") {
        this.renderSliderSlides(this.activeFilter);
        this.initSwiper();
        this.bindResize();
      } else {
        this.applyGridFilter(this.activeFilter);
      }

      this.buttonCursor = section.querySelector(".popular-products-cursor");

      if (this.buttonCursor) {
        this.initCursor();
      }
    }

    buildBreakpointMap() {
      const steps = [
        { bp: 0, cols: 1 },
        { bp: 750, cols: 2 },
        { bp: 1200, cols: 3 },
        { bp: 1536, cols: 4 },
      ];

      return steps.filter((step) => step.cols <= this.perRow);
    }

    getCurrentCols() {
      const viewportWidth = window.innerWidth;
      let cols = 1;

      this.breakpointMap.forEach((step) => {
        if (viewportWidth >= step.bp) {
          cols = step.cols;
        }
      });

      return cols;
    }

    normalizeFilter(filterTarget) {
      if (!filterTarget || filterTarget === "_all") {
        return null;
      }

      return filterTarget;
    }

    getInitialFilter() {
      const activeFilterItem = this.filterItems.find((item) =>
        item.classList.contains("active")
      );
      const defaultFilterHandle = this.layoutEl.dataset.defaultFilterHandle;

      return this.normalizeFilter(
        activeFilterItem?.dataset.filterTarget || defaultFilterHandle
      );
    }

    matchesFilter(item, filterTarget) {
      if (!filterTarget) {
        return true;
      }

      const productFilter = item.dataset.productFilter || "";
      const filterValues = productFilter
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

      return filterValues.includes(filterTarget);
    }

    setActiveFilter(filterItem) {
      this.filterItems.forEach((item) => item.classList.remove("active"));

      if (filterItem) {
        filterItem.classList.add("active");
      }
    }

    applyGridFilter(filterTarget) {
      this.allSlides.forEach((item) => {
        item.hidden = !this.matchesFilter(item, filterTarget);
      });
    }

    renderSliderSlides(filterTarget) {
      const fragment = document.createDocumentFragment();
      const filteredSlides = this.allSlides.filter((item) =>
        this.matchesFilter(item, filterTarget)
      );

      this.wrapper.innerHTML = "";

      filteredSlides.forEach((item) => {
        item.hidden = false;
        fragment.appendChild(item);
      });

      this.wrapper.appendChild(fragment);
    }

    updateOverflow() {
      if (this.layout !== "slider") {
        return;
      }

      const visibleSlides = this.wrapper.querySelectorAll(
        ".popular-products__card"
      ).length;
      const currentCols = this.getCurrentCols();

      this.layoutEl.classList.toggle(
        "has-overflow",
        visibleSlides > currentCols
      );
    }

    initSwiper() {
      if (this.layout !== "slider" || !this.wrapper.children.length) {
        this.layoutEl.classList.remove("has-overflow");
        return;
      }

      // No `observer`/`observeParents`: MutationObservers on every ancestor re-ran a full swiper
      // update on unrelated DOM churn. Size changes are covered by bindResize's ResizeObserver,
      // content changes by the destroy/re-init in switchFilter.
      // Mobile centers the intermediate slides; `centeredSlidesBounds` keeps the first
      // one flush left and the last flush right. Both must be switched off explicitly
      // at 750 — breakpoint params merge over the base set, they don't reset it.
      this.swiper = new Swiper(this.layoutEl, {
        slidesPerView: "auto",
        spaceBetween: 16,
        speed: 1000,
        watchOverflow: true,
        centeredSlides: true,
        centeredSlidesBounds: true,
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints: {
          750: {
            spaceBetween: 24,
            centeredSlides: false,
            centeredSlidesBounds: false,
          },
        },
        on: {
          init: () => {
            this.updateOverflow();
          },
          resize: () => {
            this.updateOverflow();
          },
        },
      });
    }

    destroySwiper() {
      const swiperInstance = this.swiper || this.layoutEl.swiper;

      if (swiperInstance && typeof swiperInstance.destroy === "function") {
        swiperInstance.destroy(true, true);
      }

      this.swiper = null;
    }

    switchFilter(filterTarget) {
      const nextFilter = this.normalizeFilter(filterTarget);

      if (this.activeFilter === nextFilter) {
        return;
      }

      this.activeFilter = nextFilter;

      if (this.layout !== "slider") {
        this.applyGridFilter(this.activeFilter);
        return;
      }

      this.destroySwiper();
      this.renderSliderSlides(this.activeFilter);
      this.initSwiper();
    }

    bindFilters() {
      if (!this.filterItems.length) {
        return;
      }

      this.filterItems.forEach((filterItem) => {
        filterItem.addEventListener("click", () => {
          if (filterItem.classList.contains("active")) {
            return;
          }

          this.setActiveFilter(filterItem);
          this.switchFilter(filterItem.dataset.filterTarget);
        });
      });
    }

    bindResize() {
      if (typeof ResizeObserver === "undefined") {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.swiper) {
          return;
        }

        this.swiper.update();
        this.updateOverflow();

        if (this.buttonCursor) {
          this.cursorLayoutRect = this.layoutEl.getBoundingClientRect();
        }
      });

      this.resizeObserver.observe(this.layoutEl);
    }

    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.destroySwiper();

      if (this.buttonCursor) {
        this.deactivateCursor();
      }
    }

    // ─── cursor ───────────────────────────────────────────────────────────────

    initCursor() {
      this.cursorTargetX = 0;
      this.cursorTargetY = 0;
      this.cursorX = 0;
      this.cursorY = 0;
      this.cursorRaf = null;
      this.cursorLayoutRect = null;
      this.cursorVisible = false;
      this.cursorPositioned = false;
      this.cursorActive = false;

      this.cursorMediaQuery =
        typeof window.matchMedia === "function"
          ? window.matchMedia(
              "(min-width: 1160px) and (hover: hover) and (pointer: fine)"
            )
          : null;

      this.cursorHandleMove = this.onCursorMove.bind(this);
      this.cursorHandleLeave = this.onCursorLeave.bind(this);
      this.cursorHandlePointerDown = this.onCursorPointerDown.bind(this);
      this.cursorHandlePointerUp = this.onCursorPointerUp.bind(this);
      this.cursorHandleScroll = this.onCursorScroll.bind(this);
      this.cursorTick = this.onTick.bind(this);

      this.activateCursor();
    }

    isCursorEnabled() {
      return (
        Boolean(this.layoutEl) &&
        (!this.cursorMediaQuery || this.cursorMediaQuery.matches)
      );
    }

    activateCursor() {
      if (this.cursorActive || !this.isCursorEnabled()) return;

      this.cursorActive = true;
      this.cursorLayoutRect = this.layoutEl.getBoundingClientRect();

      this.layoutEl.addEventListener("pointermove", this.cursorHandleMove);
      this.layoutEl.addEventListener("mouseleave", this.cursorHandleLeave);
      this.layoutEl.addEventListener("pointerdown", this.cursorHandlePointerDown);
      this.layoutEl.addEventListener("pointerup", this.cursorHandlePointerUp);
      window.addEventListener("scroll", this.cursorHandleScroll, {
        passive: true,
      });
    }

    deactivateCursor() {
      this.layoutEl.removeEventListener("pointermove", this.cursorHandleMove);
      this.layoutEl.removeEventListener("mouseleave", this.cursorHandleLeave);
      this.layoutEl.removeEventListener("pointerdown", this.cursorHandlePointerDown);
      this.layoutEl.removeEventListener("pointerup", this.cursorHandlePointerUp);
      window.removeEventListener("scroll", this.cursorHandleScroll);

      if (this.cursorRaf !== null) {
        cancelAnimationFrame(this.cursorRaf);
        this.cursorRaf = null;
      }

      this.hideCursor();
      this.cursorLayoutRect = null;
      this.cursorPositioned = false;
      this.cursorActive = false;
    }

    showCursor() {
      if (this.cursorVisible) return;
      this.buttonCursor.classList.add("is-visible");
      this.layoutEl.classList.add("button-cursor-shown");
      this.cursorVisible = true;
    }

    hideCursor() {
      if (!this.cursorVisible) return;
      this.buttonCursor.classList.remove("is-visible");
      this.layoutEl.classList.remove("button-cursor-shown");
      this.cursorVisible = false;
    }

    onCursorPointerDown() {
      this.buttonCursor.classList.add("is-dragging");
    }

    onCursorPointerUp() {
      this.buttonCursor.classList.remove("is-dragging");
    }

    onCursorScroll() {
      this.cursorLayoutRect = this.layoutEl.getBoundingClientRect();
    }

    onCursorMove(event) {
      const rect = this.cursorLayoutRect;
      this.cursorTargetX = event.clientX - rect.left;
      this.cursorTargetY = event.clientY - rect.top;

      if (!this.cursorPositioned) {
        this.cursorX = this.cursorTargetX;
        this.cursorY = this.cursorTargetY;
        this.cursorPositioned = true;
      }

      if (!this.cursorRaf) {
        this.cursorRaf = requestAnimationFrame(this.cursorTick);
      }

      if (event.target.closest(".product-card")) {
        this.hideCursor();
      } else {
        this.showCursor();
      }
    }

    onCursorLeave() {
      this.hideCursor();
      cancelAnimationFrame(this.cursorRaf);
      this.cursorRaf = null;
      this.cursorPositioned = false;
    }

    onTick() {
      const ease = 0.25;
      this.cursorX += (this.cursorTargetX - this.cursorX) * ease;
      this.cursorY += (this.cursorTargetY - this.cursorY) * ease;
      this.buttonCursor.style.transform = `translate3d(${this.cursorX}px, ${this.cursorY}px, 0) translate(-50%, -50%)`;
      this.cursorRaf = requestAnimationFrame(this.cursorTick);
    }
  }

  // Exposed so the Product recommendations section can reuse the exact same
  // slider/cursor/layout engine on its async-loaded content.
  window.PopularProductsSection = PopularProductsSection;

  const initPopularProductsSection = (section) => {
    if (!section || !section.classList.contains("popular-products-section")) {
      return;
    }

    const existingInstance = sectionInstances.get(section);

    if (existingInstance) {
      existingInstance.destroy();
    }

    sectionInstances.set(section, new PopularProductsSection(section));
  };

  // Defer the whole setup until the section approaches the viewport (400px ahead). The observer
  // fires immediately for sections already on screen, so above-the-fold instances initialize
  // right away; below-the-fold ones keep their Swiper init cost out of the page-load window.
  const lazyInitSection = (section) => {
    if (!section || !section.classList.contains("popular-products-section")) {
      return;
    }
    if (typeof IntersectionObserver !== "function") {
      initPopularProductsSection(section);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        initPopularProductsSection(section);
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(section);
  };

  lazyInitSection(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    lazyInitSection(event.target);
  });
})();
